import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Lock, ChevronRight, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fruitCrush_token";

function api(path, opts = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...opts.headers },
  }).then((r) => r.json());
}

// Character portraits with color theme
const PORTRAITS = {
  narrator: { bg: "from-slate-600 to-slate-800", emoji: "📜", name: "Narrator" },
  lumi: { bg: "from-amber-400 to-yellow-600", emoji: "✨", name: "Lumi" },
  lumi_happy: { bg: "from-amber-400 to-yellow-600", emoji: "😊", name: "Lumi" },
  lumi_worried: { bg: "from-amber-400 to-orange-600", emoji: "😟", name: "Lumi" },
  lumi_shocked: { bg: "from-amber-400 to-red-500", emoji: "😲", name: "Lumi" },
  lumi_excited: { bg: "from-amber-400 to-pink-500", emoji: "🤩", name: "Lumi" },
  lumi_determined: { bg: "from-amber-500 to-orange-700", emoji: "😤", name: "Lumi" },
  coral: { bg: "from-cyan-500 to-blue-700", emoji: "🏴‍☠️", name: "Captain Coral" },
  frostina: { bg: "from-blue-300 to-indigo-600", emoji: "❄️", name: "Frostina" },
  ignis: { bg: "from-red-500 to-orange-700", emoji: "🔥", name: "Ignis" },
  ignis_surprised: { bg: "from-red-400 to-orange-600", emoji: "😮", name: "Ignis" },
  elder_spore: { bg: "from-green-600 to-emerald-800", emoji: "🍄", name: "Elder Spore" },
  shade: { bg: "from-purple-700 to-slate-900", emoji: "🌑", name: "Shade" },
  shade_sad: { bg: "from-purple-600 to-slate-800", emoji: "😔", name: "Shade" },
  tempest: { bg: "from-yellow-500 to-blue-700", emoji: "⚡", name: "Tempest" },
  fig: { bg: "from-amber-600 to-brown-700", emoji: "📚", name: "Professor Fig" },
  crystalia: { bg: "from-pink-400 to-violet-600", emoji: "💎", name: "Crystalia" },
  luna: { bg: "from-indigo-400 to-purple-700", emoji: "🌙", name: "Luna" },
  sandstorm: { bg: "from-yellow-600 to-amber-800", emoji: "🏜️", name: "Sandstorm" },
  drako: { bg: "from-red-600 to-orange-800", emoji: "🐉", name: "Drako" },
  void_worm: { bg: "from-purple-900 to-black", emoji: "🪱", name: "Void Worm" },
};

const ACT_COLORS = {
  I: "from-emerald-500/20 to-cyan-500/20 border-emerald-500/20",
  II: "from-amber-500/20 to-orange-500/20 border-amber-500/20",
  III: "from-red-500/20 to-purple-500/20 border-red-500/20",
};

// ===  Chapter List View ===
function ChapterList({ chapters, onSelect }) {
  const acts = {};
  chapters.forEach((ch) => {
    if (!acts[ch.act]) acts[ch.act] = [];
    acts[ch.act].push(ch);
  });

  const actNames = { I: "Act I: The Awakening", II: "Act II: The Gathering Dark", III: "Act III: The Final Crush" };

  return (
    <div className="space-y-6">
      {Object.entries(acts).map(([act, chs]) => (
        <div key={act}>
          <h2 className="text-sm font-bold text-white/70 uppercase tracking-wider mb-3 flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> {actNames[act] || `Act ${act}`}
          </h2>
          <div className="space-y-2">
            {chs.map((ch) => (
              <button
                key={ch.chapter_id}
                onClick={() => ch.unlocked && onSelect(ch.chapter_id)}
                disabled={!ch.unlocked}
                className={`w-full p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${
                  ch.unlocked
                    ? `bg-gradient-to-r ${ACT_COLORS[act] || ""} hover:brightness-110 active:scale-[0.99]`
                    : "bg-slate-800/20 border-slate-700/20 opacity-40 cursor-not-allowed"
                }`}
                data-testid={`story-chapter-${ch.chapter_id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                  ch.completed ? "bg-emerald-500/20" : ch.unlocked ? "bg-white/10" : "bg-slate-800"
                }`}>
                  {ch.completed ? (
                    <Check className="w-5 h-5 text-emerald-400" />
                  ) : ch.unlocked ? (
                    <BookOpen className="w-5 h-5 text-white/70" />
                  ) : (
                    <Lock className="w-5 h-5 text-slate-600" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold text-sm ${ch.unlocked ? "text-white" : "text-slate-600"}`}>
                    {ch.unlocked ? ch.title : "???"}
                  </h3>
                  <div className="text-[10px] text-slate-400">
                    {ch.scene_count} scenes{ch.has_choice ? " | Has player choice" : ""}
                    {!ch.unlocked ? ` | Unlock at World ${ch.world + 1}` : ""}
                  </div>
                </div>
                {ch.unlocked && <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// === Cutscene Player ===
function CutscenePlayer({ chapter, onComplete, onBack }) {
  const [sceneIndex, setSceneIndex] = useState(0);
  const [textRevealed, setTextRevealed] = useState(false);
  const [choiceMade, setChoiceMade] = useState(chapter.choice_made);
  const [reward, setReward] = useState(null);
  const [finishing, setFinishing] = useState(false);

  const scene = chapter.scenes[sceneIndex];
  const portrait = PORTRAITS[scene?.portrait] || PORTRAITS.narrator;
  const isLastScene = sceneIndex >= chapter.scenes.length - 1;
  const hasChoice = scene?.choice && choiceMade === null;

  useEffect(() => {
    setTextRevealed(false);
    const timer = setTimeout(() => setTextRevealed(true), 50);
    return () => clearTimeout(timer);
  }, [sceneIndex]);

  const handleNext = () => {
    if (isLastScene) {
      handleComplete();
    } else {
      setSceneIndex((i) => i + 1);
    }
  };

  const handleChoice = async (idx) => {
    setChoiceMade(idx);
    setFinishing(true);
    const res = await api("/story/complete", {
      method: "POST",
      body: JSON.stringify({ chapter_id: chapter.chapter_id, choice_index: idx }),
    });
    if (res.success) {
      setReward(res.reward);
    }
    setFinishing(false);
  };

  const handleComplete = async () => {
    if (!chapter.completed && choiceMade === null) {
      // No choice in this chapter, just mark complete
      setFinishing(true);
      await api("/story/complete", {
        method: "POST",
        body: JSON.stringify({ chapter_id: chapter.chapter_id, choice_index: -1 }),
      });
      setFinishing(false);
    }
    onComplete();
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="cutscene-player">
      {/* Scene background overlay */}
      <div className="flex-1 flex flex-col justify-end relative overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-b ${portrait.bg} opacity-10`} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-transparent" />

        {/* Chapter title */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
          <button onClick={onBack} className="text-slate-400 text-xs flex items-center gap-1 hover:text-white transition-colors" data-testid="cutscene-back">
            <ArrowLeft className="w-3 h-3" /> Exit
          </button>
          <span className="text-xs text-slate-500">Act {chapter.act} | {sceneIndex + 1}/{chapter.scenes.length}</span>
        </div>

        {/* Reward notification */}
        {reward && Object.keys(reward).length > 0 && (
          <div className="absolute top-12 left-1/2 -translate-x-1/2 z-20 px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl text-white text-xs font-bold flex items-center gap-2 shadow-xl animate-bounce">
            <Sparkles className="w-4 h-4" />
            {reward.coins && `+${reward.coins} coins`}
            {reward.xp && `+${reward.xp} XP`}
            {reward.item && ` +${reward.item}`}
          </div>
        )}

        {/* Main dialogue area */}
        <div className="relative z-10 px-4 pb-6 max-w-2xl mx-auto w-full">
          {/* Portrait */}
          <div className="flex items-end gap-3 mb-3">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${portrait.bg} flex items-center justify-center text-2xl shadow-lg shrink-0`}>
              {portrait.emoji}
            </div>
            <div>
              <span className="text-xs font-bold text-white/60 uppercase tracking-wider">{scene.speaker}</span>
            </div>
          </div>

          {/* Text box */}
          <Card className="bg-slate-900/80 backdrop-blur-md border-white/10 p-4 mb-4">
            <p className={`text-white text-sm leading-relaxed transition-opacity duration-300 ${textRevealed ? "opacity-100" : "opacity-0"}`}>
              {scene.text}
            </p>
          </Card>

          {/* Choice options */}
          {hasChoice && (
            <div className="space-y-2 mb-4">
              <p className="text-xs text-amber-300 font-bold">{scene.choice.prompt}</p>
              {scene.choice.options.map((opt, idx) => (
                <button
                  key={idx}
                  onClick={() => handleChoice(idx)}
                  disabled={finishing}
                  className="w-full p-3 rounded-xl bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-500/30 text-white text-sm text-left hover:border-amber-500/50 hover:from-purple-900/60 hover:to-indigo-900/60 transition-all active:scale-[0.99]"
                  data-testid={`story-choice-${idx}`}
                >
                  <span className="text-amber-400 font-bold mr-2">{String.fromCharCode(65 + idx)}.</span>
                  {opt.text}
                  {opt.reward?.coins && <span className="text-yellow-400 text-xs ml-2">(+{opt.reward.coins} coins)</span>}
                  {opt.reward?.xp && <span className="text-cyan-400 text-xs ml-2">(+{opt.reward.xp} XP)</span>}
                  {opt.reward?.item && <span className="text-green-400 text-xs ml-2">(+item)</span>}
                </button>
              ))}
            </div>
          )}

          {/* Already chosen */}
          {scene?.choice && choiceMade !== null && (
            <div className="mb-4 p-3 rounded-xl bg-emerald-900/20 border border-emerald-500/20">
              <p className="text-xs text-emerald-400 font-bold mb-1">Your choice:</p>
              <p className="text-sm text-white">{scene.choice.options[choiceMade]?.text}</p>
            </div>
          )}

          {/* Next / Finish button */}
          {!hasChoice && (
            <Button
              onClick={handleNext}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:brightness-110 text-white"
              data-testid="cutscene-next"
            >
              {isLastScene ? "Finish Chapter" : "Continue"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// === Main Story Page ===
export default function StoryPage() {
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChapter, setActiveChapter] = useState(null);

  const loadChapters = useCallback(async () => {
    const data = await api("/story/chapters");
    setChapters(data.chapters || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadChapters(); }, [loadChapters]);

  const loadChapter = async (chapterId) => {
    const data = await api(`/story/chapter/${chapterId}`);
    if (data.scenes) setActiveChapter(data);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950/20 to-slate-950 flex items-center justify-center">
        <BookOpen className="w-12 h-12 text-purple-400 animate-bounce" />
      </div>
    );
  }

  if (activeChapter) {
    return (
      <CutscenePlayer
        chapter={activeChapter}
        onComplete={() => { setActiveChapter(null); loadChapters(); }}
        onBack={() => setActiveChapter(null)}
      />
    );
  }

  const completedCount = chapters.filter((c) => c.completed).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-purple-950/20 to-slate-950 pb-16">
      <div className="container mx-auto px-3 py-4 max-w-2xl">
        <header className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)} className="text-slate-300 flex items-center gap-1 text-sm hover:text-white transition-colors" data-testid="story-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="font-bold text-lg text-purple-300 flex items-center gap-1.5">
            <BookOpen className="w-5 h-5" /> Story Mode
          </h1>
          <span className="text-xs text-slate-500">{completedCount}/{chapters.length}</span>
        </header>

        <p className="text-xs text-slate-400 text-center mb-6">
          Follow the epic tale of Fruittopia. Make choices that shape the story and earn exclusive rewards.
        </p>

        <ChapterList chapters={chapters} onSelect={loadChapter} />
      </div>
    </div>
  );
}
